import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Typography,
} from "@mui/material";
import {
  BadgeOutlined,
  CalendarMonthOutlined,
  CallOutlined,
  FemaleOutlined,
  MaleOutlined,
  PaymentsOutlined,
  PersonOutlineOutlined,
  SchoolOutlined,
  WorkOutlineOutlined,
} from "@mui/icons-material";
import UserDetailsPageTable from "./UserDetailsPageTable";
import "./UserDetailsPage.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"
).replace(/\/$/, "");

const safeValue = (value, fallback = "Not provided") => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "null" ||
    value === "undefined"
  ) {
    return fallback;
  }

  return value;
};

const getImageUrl = (image) => {
  if (!image) return "";

  if (
    image.startsWith("http://") ||
    image.startsWith("https://") ||
    image.startsWith("blob:")
  ) {
    return image;
  }

  return `${API_BASE_URL}${image.startsWith("/") ? image : `/${image}`}`;
};

const formatDate = (value) => {
  if (!value) return "Not provided";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatMoney = (value, currency = "LRD") => {
  const amount = Number(value);

  if (Number.isNaN(amount)) {
    return `${currency} 0.00`;
  }

  return `${currency} ${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getUserTypeLabel = (value) => {
  if (!value) return "User";

  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const SectionHeader = ({
  eyebrow,
  title,
  icon,
  rightContent,
}) => (
  <div className="details-section-header">
    <div className="details-section-heading">
      <div className="details-section-icon">{icon}</div>

      <div>
        <div className="details-section-eyebrow">{eyebrow}</div>
        <div className="details-section-title">{title}</div>
      </div>
    </div>

    {rightContent && (
      <div className="details-section-action">
        {rightContent}
      </div>
    )}
  </div>
);

const InformationItem = ({
  label,
  value,
  accent = false,
  fullWidth = false,
}) => (
  <div
    className={`information-item ${
      fullWidth ? "information-item-full" : ""
    }`}
  >
    <div className="information-label">{label}</div>

    <div
      className={`information-value ${
        accent ? "information-value-accent" : ""
      }`}
    >
      {safeValue(value)}
    </div>
  </div>
);

const Metric = ({ label, value, caption }) => (
  <div className="profile-metric">
    <div className="profile-metric-label">{label}</div>
    <div className="profile-metric-value">{safeValue(value, "—")}</div>
    {caption && (
      <div className="profile-metric-caption">{caption}</div>
    )}
  </div>
);

const UserDetailsPage = ({ selectedUser }) => {
  const user = selectedUser || {};

  const studentData = user.student_data || {};
  const teacherData = user.teacher_data || {};
  const payrollData = user.payroll_data || {};

  const payments = useMemo(() => {
    if (Array.isArray(user.payments)) {
      return user.payments;
    }

    if (Array.isArray(user.payments?.results)) {
      return user.payments.results;
    }

    return [];
  }, [user.payments]);

  const payrolls = useMemo(() => {
    if (Array.isArray(user.payrolls)) {
      return user.payrolls;
    }

    if (Array.isArray(user.payrolls?.results)) {
      return user.payrolls.results;
    }

    return [];
  }, [user.payrolls]);

  const isStudent =
    String(user.user_type || "").toLowerCase() === "student";

  const isTeacher =
    String(user.user_type || "").toLowerCase() === "teacher";

  const isSupportStaff =
    Boolean(user.is_supportstaff) ||
    String(user.user_type || "").toLowerCase() === "supportstaff";

  const fullName = safeValue(user.full_name, "Unnamed User");

  const profileImage = getImageUrl(
    user.profile_image || user.image
  );

  const userType = getUserTypeLabel(user.user_type);

  const studentGrade =
    studentData.grade_class_name ||
    studentData.grade_class ||
    "Not assigned";

  const studentDivision =
    studentData.division_assigned_name ||
    studentData.division_name ||
    "Not assigned";

  const studentGender =
    studentData.gender ||
    user.gender ||
    "Not provided";

  const totalPaidLRD = useMemo(
    () =>
      payments.reduce(
        (total, payment) =>
          total + Number(payment.amount_paid_lrd || 0),
        0
      ),
    [payments]
  );

  const latestPayment = payments.length
    ? [...payments].sort(
        (a, b) =>
          new Date(b.created_at || b.date || 0) -
          new Date(a.created_at || a.date || 0)
      )[0]
    : null;

  return (
    <Box className="user-details-page">
      <Box className="user-details-container">

        {/* =========================================================
            PROFILE HEADER
        ========================================================== */}
        <section className="profile-command-header">

          <div className="profile-command-pattern" />

          <div className="profile-command-content">

            <div className="profile-identity-column">

              <div className="profile-avatar-container">
                <div className="profile-avatar-glow" />

                <Avatar
                  src={profileImage || undefined}
                  alt={fullName}
                  className="profile-main-avatar"
                >
                  {!profileImage &&
                    fullName
                      .split(" ")
                      .slice(0, 2)
                      .map((name) => name.charAt(0))
                      .join("")
                      .toUpperCase()}
                </Avatar>

                <div className="profile-avatar-status" />
              </div>

              <div className="profile-identity-details">

                <div className="profile-kicker">
                  USER PROFILE
                </div>

                <Typography className="profile-name">
                  {fullName}
                </Typography>

                <div className="profile-role-line">
                  <span>{userType}</span>

                  {user.email && (
                    <>
                      <span className="profile-separator">•</span>
                      <span>{user.email}</span>
                    </>
                  )}
                </div>

                <div className="profile-status-row">
                  <span className="profile-live-indicator" />

                  <span>ACCOUNT ACTIVE</span>

                  {user.date_add && (
                    <>
                      <span className="profile-separator">•</span>
                      <span>
                        Joined {formatDate(user.date_add)}
                      </span>
                    </>
                  )}
                </div>

              </div>
            </div>

            <div className="profile-command-side">

              <div className="profile-reference">
                <span>RECORD TYPE</span>
                <strong>{userType.toUpperCase()}</strong>
              </div>

              <div className="profile-reference">
                <span>RECORD DATE</span>
                <strong>
                  {formatDate(user.date_add)}
                </strong>
              </div>

            </div>
          </div>

          {/* Student command metrics */}
          {isStudent && (
            <div className="profile-metrics">

              <Metric
                label="GRADE / CLASS"
                value={studentGrade}
                caption="Current placement"
              />

              <Metric
                label="DIVISION"
                value={studentDivision}
                caption="Academic division"
              />

              <Metric
                label="GENDER"
                value={studentGender}
                caption="Student profile"
              />

              <Metric
                label="PAYMENT RECORDS"
                value={payments.length}
                caption="Recorded transactions"
              />

              <Metric
                label="TOTAL PAID"
                value={formatMoney(totalPaidLRD)}
                caption="Converted LRD"
              />

            </div>
          )}

        </section>

        {/* =========================================================
            STUDENT PROFILE
        ========================================================== */}
        {isStudent && (
          <>
            <section className="details-section">

              <SectionHeader
                eyebrow="ACADEMIC RECORD"
                title="Student Profile"
                icon={<SchoolOutlined />}
                rightContent={
                  <Chip
                    label={studentGrade}
                    className="section-chip"
                  />
                }
              />

              <div className="student-profile-layout">

                <div className="student-profile-rail">

                  <div className="student-rail-label">
                    STUDENT
                  </div>

                  <div className="student-mini-avatar">
                    <Avatar
                      src={profileImage || undefined}
                      alt={fullName}
                    >
                      {!profileImage &&
                        fullName
                          .split(" ")
                          .slice(0, 2)
                          .map((name) => name.charAt(0))
                          .join("")
                          .toUpperCase()}
                    </Avatar>
                  </div>

                  <div className="student-rail-name">
                    {fullName}
                  </div>

                  <div className="student-rail-type">
                    {userType}
                  </div>

                  <Divider className="student-rail-divider" />

                  <div className="student-rail-stat">
                    <span>Grade</span>
                    <strong>{studentGrade}</strong>
                  </div>

                  <div className="student-rail-stat">
                    <span>Division</span>
                    <strong>{studentDivision}</strong>
                  </div>

                </div>

                <div className="student-information-area">

                  <div className="information-grid">

                    <InformationItem
                      label="GRADE / CLASS"
                      value={studentGrade}
                      accent
                    />

                    <InformationItem
                      label="DIVISION"
                      value={studentDivision}
                      accent
                    />

                    <InformationItem
                      label="GENDER"
                      value={studentGender}
                    />

                    <InformationItem
                      label="DATE OF BIRTH"
                      value={formatDate(
                        studentData.date_of_birth
                      )}
                    />

                    <InformationItem
                      label="HEALTH STATUS"
                      value={studentData.health_status}
                    />

                    <InformationItem
                      label="MAJOR SUBJECT"
                      value={studentData.major_subject}
                    />

                    <InformationItem
                      label="PREVIOUS SCHOOL"
                      value={studentData.previous_school}
                    />

                    <InformationItem
                      label="PREVIOUS CLASS"
                      value={studentData.previous_class}
                    />

                  </div>

                </div>
              </div>

            </section>

            {/* =====================================================
                PARENT / GUARDIAN
            ====================================================== */}
            <section className="details-section">

              <SectionHeader
                eyebrow="FAMILY INFORMATION"
                title="Parent / Guardian"
                icon={<PersonOutlineOutlined />}
              />

              <div className="information-grid">

                <InformationItem
                  label="FATHER"
                  value={studentData.father_name}
                />

                <InformationItem
                  label="MOTHER"
                  value={studentData.mother_name}
                />

                <InformationItem
                  label="PARENT CONTACT"
                  value={studentData.parent_contact}
                  accent
                />

                <InformationItem
                  label="DEPENDENT STUDENT"
                  value={user.dependent_student}
                />

              </div>

            </section>

            {/* =====================================================
                PAYMENT HISTORY
            ====================================================== */}
            <section className="details-section payment-section">

             

              <UserDetailsPageTable
                payments={payments}
                latestPayment={latestPayment}
              />

            </section>
          </>
        )}

        {/* =========================================================
            TEACHER PROFILE
        ========================================================== */}
        {isTeacher && (
          <>
            <section className="details-section">

              <SectionHeader
                eyebrow="STAFF RECORD"
                title="Teacher Information"
                icon={<WorkOutlineOutlined />}
              />

              <div className="information-grid">

                <InformationItem
                  label="FULL NAME"
                  value={fullName}
                  accent
                />

                <InformationItem
                  label="DIVISION"
                  value={
                    teacherData.division_name ||
                    teacherData.division
                  }
                />

                <InformationItem
                  label="QUALIFICATION"
                  value={teacherData.qualification}
                />

                <InformationItem
                  label="CONTACT"
                  value={
                    teacherData.contact ||
                    user.contact
                  }
                  accent
                />

                <InformationItem
                  label="DEPENDENT STUDENT"
                  value={
                    teacherData.dependent_student ||
                    user.dependent_student
                  }
                />

                <InformationItem
                  label="SALARY"
                  value={
                    teacherData.salary ||
                    user.salary
                      ? formatMoney(
                          teacherData.salary ||
                            user.salary
                        )
                      : "Not provided"
                  }
                />

                <InformationItem
                  label="DATE ADDED"
                  value={formatDate(
                    teacherData.date_add ||
                      user.date_add
                  )}
                />

              </div>

            </section>

            <section className="details-section">

              <SectionHeader
                eyebrow="PAYROLL"
                title="Latest Payroll"
                icon={<PaymentsOutlined />}
              />

              <div className="information-grid">

                <InformationItem
                  label="MONTH"
                  value={payrollData.month}
                />

                <InformationItem
                  label="YEAR"
                  value={payrollData.year}
                />

                <InformationItem
                  label="BASIC SALARY"
                  value={
                    payrollData.basic_salary
                      ? formatMoney(
                          payrollData.basic_salary
                        )
                      : null
                  }
                />

                <InformationItem
                  label="BONUS"
                  value={
                    payrollData.bonus
                      ? formatMoney(payrollData.bonus)
                      : null
                  }
                />

                <InformationItem
                  label="DEDUCTIONS"
                  value={
                    payrollData.deductions
                      ? formatMoney(
                          payrollData.deductions
                        )
                      : null
                  }
                />

                <InformationItem
                  label="TOTAL PAID"
                  value={
                    payrollData.total_paid
                      ? formatMoney(
                          payrollData.total_paid
                        )
                      : null
                  }
                  accent
                />

              </div>

            </section>
          </>
        )}

        {/* =========================================================
            SUPPORT STAFF
        ========================================================== */}
        {isSupportStaff && !isTeacher && !isStudent && (
          <>
            <section className="details-section">

              <SectionHeader
                eyebrow="STAFF RECORD"
                title="Staff Information"
                icon={<WorkOutlineOutlined />}
              />

              <div className="information-grid">

                <InformationItem
                  label="FULL NAME"
                  value={fullName}
                  accent
                />

                <InformationItem
                  label="DEPARTMENT"
                  value={user.department}
                />

                <InformationItem
                  label="CONTACT"
                  value={user.contact}
                  accent
                />

                <InformationItem
                  label="DEPENDENT STUDENT"
                  value={user.dependent_student}
                />

                <InformationItem
                  label="SALARY"
                  value={
                    user.salary
                      ? formatMoney(user.salary)
                      : null
                  }
                />

                <InformationItem
                  label="DATE ADDED"
                  value={formatDate(user.date_add)}
                />

              </div>

            </section>

            <section className="details-section">

              <SectionHeader
                eyebrow="PAYROLL HISTORY"
                title="Payroll Records"
                icon={<PaymentsOutlined />}
              />

              {payrolls.length === 0 ? (
                <div className="empty-state">
                  <PaymentsOutlined />
                  <strong>No payroll records</strong>
                  <span>
                    No payroll history is currently available
                    for this staff member.
                  </span>
                </div>
              ) : (
                <div className="payroll-history">

                  {payrolls.map((payroll, index) => (
                    <div
                      className="payroll-row"
                      key={
                        payroll.id ||
                        `${payroll.year}-${payroll.month}-${index}`
                      }
                    >

                      <div className="payroll-period">
                        <span>
                          {safeValue(payroll.month)}
                        </span>
                        <strong>
                          {safeValue(payroll.year)}
                        </strong>
                      </div>

                      <div className="payroll-value">
                        <span>Basic Salary</span>
                        <strong>
                          {formatMoney(
                            payroll.basic_salary
                          )}
                        </strong>
                      </div>

                      <div className="payroll-value">
                        <span>Bonus</span>
                        <strong>
                          {formatMoney(payroll.bonus)}
                        </strong>
                      </div>

                      <div className="payroll-value">
                        <span>Deductions</span>
                        <strong>
                          {formatMoney(
                            payroll.deductions
                          )}
                        </strong>
                      </div>

                      <div className="payroll-value payroll-total">
                        <span>Total Paid</span>
                        <strong>
                          {formatMoney(
                            payroll.total_paid
                          )}
                        </strong>
                      </div>

                    </div>
                  ))}

                </div>
              )}

            </section>
          </>
        )}

        {/* =========================================================
            GENERAL CONTACT INFORMATION
        ========================================================== */}
        {!isStudent && (
          <section className="details-section">

            <SectionHeader
              eyebrow="CONTACT"
              title="Contact Information"
              icon={<CallOutlined />}
            />

            <div className="information-grid">

              <InformationItem
                label="EMAIL"
                value={user.email}
                accent
              />

              <InformationItem
                label="PHONE / CONTACT"
                value={user.contact}
                accent
              />

              <InformationItem
                label="USER TYPE"
                value={userType}
              />

              <InformationItem
                label="DATE ADDED"
                value={formatDate(user.date_add)}
              />

            </div>

          </section>
        )}

        <footer className="user-details-footer">
          <span>POLU INTEGRATED SCHOOL MANAGEMENT SYSTEM</span>
          <span>USER RECORD • {userType.toUpperCase()}</span>
        </footer>

      </Box>
    </Box>
  );
};

export default UserDetailsPage;